import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from '@tanstack/react-router';
import { Route } from '@/routes/_public/signup';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@/lib/zod-resolver';
import { z } from 'zod';
import { Eye, EyeOff } from 'lucide-react';
import { signIn, signUp } from '@/lib/auth-client';
import { GOOGLE_CLIENT_ID } from '@/lib/env';
import { GoogleIcon } from '@/components/icons/google-icon';
import { PasswordStrengthMeter, evaluatePassword } from '@/components/password-strength-meter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

const signupSchema = z
  .object({
    name: z.string().min(1, 'Name is required'),
    email: z.string().email('Invalid email address'),
    password: z.string().min(12, 'Password must be at least 12 characters'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type SignupForm = z.infer<typeof signupSchema>;

export default function Signup(): React.ReactElement {
  const navigate = useNavigate();
  const searchParams = Route.useSearch();
  const invitationToken = searchParams.token ?? null;

  const oauthError = searchParams.error;
  const oauthErrorMessage = oauthError ? 'Google sign up failed. Please try again.' : null;
  const [error, setError] = useState<string | null>(oauthErrorMessage);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [invitationEmail, setInvitationEmail] = useState<string | null>(null);
  const [tokenLoading, setTokenLoading] = useState(!!invitationToken);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    setError: setFieldError,
    watch,
    formState: { errors },
  } = useForm<SignupForm>({
    resolver: zodResolver(signupSchema),
  });

  const passwordValue = watch('password', '');
  const nameValue = watch('name', '');
  const emailValue = watch('email', '');
  const userInputs = useMemo(
    () => [nameValue, emailValue].filter(Boolean),
    [nameValue, emailValue]
  );

  // Validate invitation token before rendering form
  useEffect(() => {
    if (invitationToken) {
      fetch(`/api/auth/invitation/${invitationToken}`)
        .then((res) => res.json())
        .then((data) => {
          if (data.success && data.data.valid) {
            setInvitationEmail(data.data.email);
            setValue('email', data.data.email);
          }
        })
        .catch(() => {
          // Ignore error - user can still sign up
        })
        .finally(() => setTokenLoading(false));
    }
  }, [invitationToken, setValue]);

  const handleGoogleSignUp = async (): Promise<void> => {
    setIsGoogleLoading(true);
    setError(null);
    try {
      await signIn.social({
        provider: 'google',
        callbackURL: window.location.origin,
        errorCallbackURL: window.location.origin + '/signup',
        requestSignUp: true,
      });
    } catch {
      setError('Google sign up failed');
      setIsGoogleLoading(false);
    }
  };

  const onSubmit = async (data: SignupForm): Promise<void> => {
    // Check password strength via zxcvbn
    const strength = evaluatePassword(data.password, [data.name, data.email].filter(Boolean));
    if (!strength.isStrong) {
      setFieldError('password', {
        message: 'Password is too weak. Try a longer or more unique password.',
      });
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await signUp.email({
        email: data.email,
        password: data.password,
        name: data.name,
      } as Parameters<typeof signUp.email>[0]);

      if (result.error) {
        const msg = result.error.message?.toLowerCase() || '';
        if (msg.includes('already') || msg.includes('exists') || msg.includes('duplicate')) {
          setError('An account with this email already exists. Please sign in instead.');
        } else {
          setError(result.error.message || 'Sign up failed');
        }
        return;
      }

      // Redirect to login with success message — user must verify email first
      navigate({ to: '/login', search: { signup: 'success' } });
    } catch {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  if (tokenLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm border-0 shadow-lg">
        <CardHeader className="space-y-2 pb-2">
          <p className="text-sm font-semibold tracking-wide text-muted-foreground">VagusX</p>
          <CardTitle className="text-2xl font-bold">
            {invitationToken ? 'Join your team' : 'Create an account'}
          </CardTitle>
          <CardDescription>
            {invitationToken
              ? 'Complete your account setup to get started'
              : 'Create your account to get started'}
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <CardContent className="space-y-5">
            {error && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="John Doe"
                {...register('name')}
                disabled={isLoading}
              />
              {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="name@example.com"
                {...register('email')}
                disabled={isLoading || !!invitationEmail}
              />
              {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a password"
                  {...register('password')}
                  disabled={isLoading}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <PasswordStrengthMeter password={passwordValue} userInputs={userInputs} />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm your password"
                  {...register('confirmPassword')}
                  disabled={isLoading}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>
              )}
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? 'Creating account...' : 'Create account'}
            </Button>
            {GOOGLE_CLIENT_ID && (
              <>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleGoogleSignUp}
                  disabled={isGoogleLoading}
                >
                  <GoogleIcon className="mr-2 h-4 w-4" />
                  {isGoogleLoading ? 'Redirecting...' : 'Continue with Google'}
                </Button>
              </>
            )}
          </CardContent>
          <CardFooter className="justify-center pt-2">
            <p className="text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link to="/login" className="text-primary hover:underline">
                Sign in
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
